import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CATEGORY_KEYS } from "../lib/i18n";

export default function Shop() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const brand = params.get("brand") || "all";
  const q = params.get("q") || "";
  const on_offer = params.get("on_offer") === "true";
  const sort = params.get("sort") || "newest";

  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/meta").then(({ data }) => setMeta(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category && category !== "all") p.category = category;
    if (brand && brand !== "all") p.brand = brand;
    if (q) p.q = q;
    if (on_offer) p.on_offer = true;
    api
      .get("/products", { params: p })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, brand, q, on_offer]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price_asc") arr.sort((a, b) => a.final_price - b.final_price);
    else if (sort === "price_desc") arr.sort((a, b) => b.final_price - a.final_price);
    return arr;
  }, [products, sort]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "" || v === "all" || v === false) next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next);
  };

  return (
    <main data-testid="shop-page" className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">{t("shop")}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">
          {on_offer ? t("onOffer") : lang === "ar" ? "كل المنتجات" : "All products"}
        </h1>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="category-chips">
        <Chip active={category === "all"} onClick={() => update({ category: "all" })} label={t("all")} testId="chip-all" />
        {CATEGORY_KEYS.map((c) => (
          <Chip
            key={c}
            active={category === c}
            onClick={() => update({ category: c })}
            label={t(c)}
            testId={`chip-${c}`}
          />
        ))}
      </div>

      {/* Filter row */}
      <div className="grid sm:grid-cols-[1fr,180px,180px] gap-3 mb-8">
        <Input
          value={q}
          onChange={(e) => update({ q: e.target.value })}
          placeholder={t("searchPlaceholder")}
          data-testid="search-input"
          className="h-11 bg-white"
        />
        <Select value={brand} onValueChange={(v) => update({ brand: v })}>
          <SelectTrigger data-testid="brand-select" className="h-11 bg-white">
            <SelectValue placeholder={t("brandLabel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            {meta.brands.map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => update({ sort: v })}>
          <SelectTrigger data-testid="sort-select" className="h-11 bg-white">
            <SelectValue placeholder={t("sortBy")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t("newest")}</SelectItem>
            <SelectItem value="price_asc">{t("priceLowHigh")}</SelectItem>
            <SelectItem value="price_desc">{t("priceHighLow")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#767676]" data-testid="shop-loading">{t("loading")}</div>
      ) : sorted.length === 0 ? (
        <div className="py-16 text-center text-[#767676]" data-testid="no-results">{t("noResults")}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="shop-grid">
          {sorted.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </main>
  );
}

function Chip({ active, onClick, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${
        active
          ? "bg-[#1A1738] text-white border-[#1A1738]"
          : "bg-white text-[#1A1738] border-[#E5E1D8] hover:bg-[#F3F0EB]"
      }`}
    >
      {label}
    </button>
  );
}
